import * as React from 'react';
import { DetailedHTMLProps, HTMLAttributes } from 'react';
import {
  AccentPalette,
  BrandPalette,
  CategoricalPalette,
  DivergingPalette,
  ErrorPalette,
  GrayscalePalette,
  SequentialPalette,
  SuccessPalette,
  WarningPalette,
} from '@tcl-boron-colors/colors';
import { mergeClasses } from '@tcl-argon-utils/utils';
import { useBadgeStyles } from './styles';

type DivElement = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface BadgeProps extends DivElement {
  className?: string;
  classes?: Record<string, string>;
  /** If `true`, render a small badge. Else, render a normal sized badge. */
  small?: boolean;
  /** determines styling of badge */
  badgeType?: 'primary' | 'secondary';
  /** icon to appear to the left of the text in the badge. Recommended to be icon from TCL */
  Icon?: React.ComponentType<{ className?: string; height: string; width: string }>;
  /** text to be displayed in the badge */
  text?: string;
  /** value to represent the badge color */
  badgeVariant?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19;
}

const badgeVariantToColorMap = new Map<number, string>([
  [1, BrandPalette[700]],
  [2, AccentPalette[100]],
  [3, GrayscalePalette[500]],
  [4, GrayscalePalette[600]],
  [5, GrayscalePalette[700]],
  [6, SuccessPalette[700]],
  [7, ErrorPalette[700]],
  [8, WarningPalette[700]],
  [9, CategoricalPalette[100]],
  [10, SequentialPalette[100]],
  [11, CategoricalPalette[300]],
  [12, CategoricalPalette[500]],
  [13, CategoricalPalette['ext600']],
  [14, CategoricalPalette[600]],
  [15, SequentialPalette[300]],
  [16, SequentialPalette[400]],
  [17, SequentialPalette[500]],
  [18, DivergingPalette[400]],
  [19, DivergingPalette[600]],
]);

const getColorForBadge = (variant: number) => {
  const color = badgeVariantToColorMap.get(variant);
  return color;
};

/**
 * Badges indicate to the user of additional information about an item they may be working on.
 * Unlike a notification, badges are not urgent and do not require immediate attention.
 * Badges differ from tags in that tags are either definitions that persist or originate from filtering and have more user involvement and control.
 *
 * @since 3.0.0
 */
export const Badge: React.FC<BadgeProps> = ({
  className = '',
  classes: classOverrides = {},
  small = false,
  badgeType = 'primary',
  Icon,
  text = '',
  badgeVariant = 1,
  ...props
}) => {
  const classes = mergeClasses(
    useBadgeStyles({ color: getColorForBadge(badgeVariant), badgeType, small }),
    classOverrides,
    {
      root: className,
    },
  );

  return (
    <div className={classes.root} data-analytics="badge" {...props}>
      {Icon && <Icon height={small ? '10px' : '12px'} width={small ? '10px' : '12px'} className={classes.icon} />}
      <span>{text}</span>
    </div>
  );
};
